import React, { useState, useMemo, useEffect, useRef } from 'react'
import { finalRank, shorten } from '../../utils/filters'
import Flag from '../common/Flag'

export default function ResultsTable({ results, status, raceinfo, current }) {
  const [sortBy, setSortBy] = useState(null)
  const [sortOrder, setSortOrder] = useState('desc')
  const [followLive, setFollowLive] = useState(true)
  const highlightedRowRef = useRef(null)
  const scrolledBibsRef = useRef(new Set())

  const sortArrow = (field) => sortBy === field ? (sortOrder === 'desc' ? ' ▼' : ' ▲') : null

  // Determine if it's a team competition
  const isTeam = raceinfo?.team === "1"

  // Sort results
  const sortedResults = useMemo(() => {
    if (!sortBy || !results) return results

    return [...results].sort((a, b) => {
      const aVal = a[sortBy] || 0
      const bVal = b[sortBy] || 0
      return sortOrder === 'desc' ? bVal - aVal : aVal - bVal
    })
  }, [results, sortBy, sortOrder])

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
  }

  // Check if row should be highlighted
  const isHighlighted = (row) => {
    if (!current) return false
    
    let bib = String(current.bib)
    if (isTeam && ['r1', 'r2', 'r3', 'r4'].includes(status)) {
      bib = String(current.bib).slice(0, -1)
    }
    
    return bib === String(row.bib)
  }

  // Scroll to highlighted row when current jumper changes (only once per athlete,
  // and only while the user has "Follow live jumper" enabled)
  useEffect(() => {
    if (followLive && highlightedRowRef.current && current) {
      const bib = String(current.bib)
      if (!scrolledBibsRef.current.has(bib)) {
        scrolledBibsRef.current.add(bib)
        highlightedRowRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      }
    }
  }, [current, followLive])

  if (!results || results.length === 0) {
    return <p>No results available</p>
  }

  return (
    <>
    <div className="flex justify-end mb-2">
      <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={followLive}
          onChange={(e) => setFollowLive(e.target.checked)}
          className="rounded border-gray-300"
        />
        Follow live jumper
      </label>
    </div>
    <table className="resultstable" cellSpacing="0" cellPadding="0" width="100%">
      <thead>
        <tr>
          <th className="text-center"><span className="hide-for-small">Rank</span></th>
          <th className="text-center hide-for-small">Bib</th>
          <th className="text-center">Q</th>
          <th>Name</th>
          <th>Nat.</th>
          <th 
            className="text-center hidden lg:table-cell" 
            style={{ cursor: 'pointer' }}
            onClick={() => handleSort('speed')}
          >
            Speed{sortArrow('speed')}
          </th>
          <th 
            className="text-center hidden lg:table-cell" 
            style={{ cursor: 'pointer' }}
            onClick={() => handleSort('gatesnumber')}
          >
            Gate{sortArrow('gatesnumber')}
          </th>
          <th 
            className="text-center" 
            style={{ cursor: 'pointer' }}
            onClick={() => handleSort('length1')}
          >
            Dist.1{sortArrow('length1')}
          </th>
          <th 
            className="text-center hide-for-small" 
            style={{ cursor: 'pointer' }}
            onClick={() => handleSort('points1')}
          >
            Pts.1{sortArrow('points1')}
          </th>
          <th 
            className="text-center" 
            style={{ cursor: 'pointer' }}
            onClick={() => handleSort('length2')}
          >
            Dist.2{sortArrow('length2')}
          </th>
          <th 
            className="text-center hide-for-small" 
            style={{ cursor: 'pointer' }}
            onClick={() => handleSort('points2')}
          >
            Pts.2{sortArrow('points2')}
          </th>
          <th 
            className="text-center" 
            style={{ cursor: 'pointer' }}
            onClick={() => handleSort('total')}
          >
            Total{sortArrow('total')}
          </th>
        </tr>
      </thead>
      <tbody>
        {sortedResults.map((row, index) => (
          <tr 
            key={index}
            ref={isHighlighted(row) ? highlightedRowRef : null}
            className={`${index % 2 === 0 ? 'even' : 'odd'} ${isHighlighted(row) ? 'highlight' : ''}`}
          >
            <td className="text-center">{finalRank(row)}</td>
            <td className="text-center hide-for-small">{row.bib}</td>
            <td 
              className={`text-center ${
                row.qualified === 'NQ' ? 'red' : row.qualified === 'Q' ? 'green' : ''
              }`}
            >
              {row.qualified}
            </td>
            <td>
              <span className="hidden md:inline">{row.name}</span>
              <span className="md:hidden">{shorten(row.name)}</span>
            </td>
            <td>
              <Flag code={row.nation} />
            </td>
            <td className="text-center extra hidden lg:table-cell">
              {row.speed ? (typeof row.speed === 'number' ? row.speed.toFixed(1) : parseFloat(row.speed).toFixed(1)) : '-'}
            </td>
            <td className="text-center extra hidden lg:table-cell">
              {row.gatesnumber ? (typeof row.gatesnumber === 'number' ? row.gatesnumber.toFixed(0) : parseFloat(row.gatesnumber).toFixed(0)) : '-'}
            </td>
            <td className="text-center">
              {row.length1 ? row.length1.toFixed(1) : '-'}
            </td>
            <td className="text-center hide-for-small">
              {row.points1 ? row.points1.toFixed(1) : '-'}
            </td>
            <td className="text-center">
              {row.length2 ? row.length2.toFixed(1) : '-'}
            </td>
            <td className="text-center hide-for-small">
              {row.points2 ? row.points2.toFixed(1) : '-'}
            </td>
            <td className="text-center">
              {row.total ? row.total.toFixed(1) : '-'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </>
  )
}
